// 정산 장부 요약 스트립 — 칸별 ⓘ 설명 팝오버 (Jin 10/20)
//
// 칸 값이 무엇이고 어떻게 나오는지를 **지금 화면의 숫자**로 풀어 쓴다.
// 숫자 클릭(원장 드로어)과 분리 — ⓘ 클릭은 stopPropagation, 열린 동안은 바깥 막(backdrop)이 화면을 덮어
// 숫자 클릭이 닿지 않는다. 닫기 = 바깥 클릭 · Escape · ⓘ 재클릭.
// 열림 상태는 부모(CancelAgencySettlement)가 칸 키 하나로 들고 있다 — 키보드로 다른 ⓘ 를 눌러도 한 번에 하나만 열린다.
// 스트립 컨테이너가 overflow:hidden(둥근 모서리)이라 팝오버는 body 포털로 띄운다.
//   데스크탑 = ⓘ 아래 앵커(좌우 클램프, 아래 공간이 모자라면 위로) / 모바일 = 바텀시트 (CancelAgencyDocReqSheet 와 같은 문법).
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

var num = function (v) { return (Number(v) || 0).toLocaleString(); };

// 칸 키별 설명 (순수). n = { month, opening, paid, grandTotal, adj, imp(엑셀 이관 조정) } — 스트립과 같은 원천 값.
// 칸 순서 = 엑셀 청구서 꼬리 (Jin 10/20) — 전월미수 → 그 달 청구(grandTotal + adj) → 그 달 입금 → 미수 (그 달 말) = 전월미수 + 청구 − 입금.
export function stripHelpContent(key, n) {
  var M = (n.month || 0) + '월';
  var adj = n.adj || 0;
  var imp = n.imp || 0;
  var charged = (n.grandTotal || 0) + adj + imp;
  var due = (n.opening || 0) + charged - (n.paid || 0);
  if (key === 'opening') return {
    body: M + " 1일 시점에 남아 있던 미수입니다. 엑셀 청구서의 '전월미수'에 해당합니다.",
    calc: '지난달 말까지 누적 청구 − 누적 입금 ± 조정 = ' + num(n.opening) + '원' };
  if (key === 'charged') return {
    body: M + ' 장부 건 합계(수기 조정' + (imp ? '·엑셀 이관 조정' : '') + ' 포함)입니다.',
    calc: '장부 합계 ' + num(n.grandTotal) + (adj ? ((adj > 0 ? ' + ' : ' − ') + '수기 조정 ' + num(Math.abs(adj))) : '') +
      (imp ? ((imp > 0 ? ' + ' : ' − ') + '엑셀 이관 조정 ' + num(Math.abs(imp))) : '') + ' = ' + num(charged) + '원',
    excel: '이번 달 청구서의 당월 합계' };
  if (key === 'paid') return {
    body: M + ' 장부에 기록된 입금 합계입니다. YK 엑셀처럼 이 달 청구서에 적힌 입금(다음 달 초 입금 포함)이 들어갈 수 있습니다.',
    excel: '이번 달 청구서의 입금 행 합계' };
  if (key === 'outstanding') return {
    body: '전월미수 + ' + M + ' 청구 − ' + M + ' 입금 입니다. 거래처가 지금 납부해야 할 금액입니다.',
    calc: num(n.opening) + ' + ' + num(charged) + ' − ' + num(n.paid) + ' = ' + num(due) + '원',
    note: due < 0 ? '음수는 선수금(초과 수금)입니다.' : '',
    excel: '이번 달 청구서의 미수합계' };
  return null;
}

// ⓘ — 칸 우상단. 히트 영역 22px 안에 14px 원.
var btnSt = { position: 'absolute', top: 4, right: 4, width: 22, height: 22, padding: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
var dotSt = { width: 14, height: 14, boxSizing: 'border-box', borderRadius: '50%', border: '1px solid #cbd5e1', background: '#fff', color: '#94a3b8', fontSize: 9, fontWeight: 700, lineHeight: '12px', textAlign: 'center' };
var veilSt = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9990 };
var sheetVeilSt = Object.assign({}, veilSt, { background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' });
var panelSt = { position: 'absolute', boxSizing: 'border-box', overflowY: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(15,30,60,0.12)', padding: '12px 14px', textAlign: 'left' };
var sheetPanelSt = { width: '100%', maxHeight: '88vh', overflowY: 'auto', boxSizing: 'border-box', background: '#fff', borderRadius: '14px 14px 0 0', padding: '16px 18px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom))', textAlign: 'left' };

// props: k(칸 키) · title(칸 라벨) · n(stripHelpContent 입력) · isMobile · open · onOpenChange(bool)
export default function SettlementStripHelp(props) {
  var [pos, setPos] = useState(null);
  var btnRef = useRef(null);
  var open = !!props.open;
  var c = stripHelpContent(props.k, props.n || {});
  var id = 'stl-strip-help-' + props.k;

  useEffect(function () {
    if (!open) return undefined;
    var onKey = function (e) {
      if (e.key !== 'Escape') return;
      props.onOpenChange(false);
      if (btnRef.current) btnRef.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return function () { document.removeEventListener('keydown', onKey); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  var toggle = function (e) {
    e.stopPropagation();
    if (open) { props.onOpenChange(false); return; }
    var r = btnRef.current.getBoundingClientRect();
    var vw = window.innerWidth, vh = window.innerHeight;
    var w = Math.min(300, vw - 16);
    var left = Math.max(8, Math.min(r.right - w, vw - w - 8));
    var below = vh - r.bottom - 14;
    setPos(below >= 160 || below >= r.top - 14
      ? { top: r.bottom + 6, left: left, width: w, maxHeight: below }
      : { bottom: vh - r.top + 6, left: left, width: w, maxHeight: r.top - 14 });
    props.onOpenChange(true);
  };

  return (<>
    <button ref={btnRef} type="button" aria-label="설명 보기" title="설명 보기" aria-expanded={open}
      aria-controls={open ? id : undefined} onClick={toggle} style={btnSt}>
      <span aria-hidden="true" style={dotSt}>i</span>
    </button>
    {open && !!c && ReactDOM.createPortal(
      <div onClick={function (e) { e.stopPropagation(); props.onOpenChange(false); }} style={props.isMobile ? sheetVeilSt : veilSt}>
        <div id={id} role="dialog" aria-label={props.title + ' 설명'} onClick={function (e) { e.stopPropagation(); }}
          style={props.isMobile ? sheetPanelSt : Object.assign({}, panelSt, pos)}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>{props.title}</div>
          <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.6 }}>{c.body}</div>
          {!!c.calc && <div style={{ marginTop: 8, padding: '6px 8px', background: '#f8fafc', borderRadius: 6, fontSize: 12.5, fontWeight: 700, color: '#1e293b', lineHeight: 1.5 }}>계산: {c.calc}</div>}
          {!!c.note && <div style={{ marginTop: 6, fontSize: 12, color: '#059669' }}>{c.note}</div>}
          {!!c.excel && <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid #f1f5f9', fontSize: 11.5, color: '#64748b' }}>엑셀로는: {c.excel}</div>}
        </div>
      </div>, document.body)}
  </>);
}
